import { useCallback, useEffect, useRef, useState } from "react";
import { CanvasArea, VideoCanvasWrapper, VideoElement } from "../style";
import DraggableFrame from "./DraggableFrame";
import { Box, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import AddRounded from "@mui/icons-material/AddRounded";
import RemoveRounded from "@mui/icons-material/RemoveRounded";
import FitScreenRounded from "@mui/icons-material/FitScreenRounded";

const CROP_RATIO = 9 / 16;

/**
 * VideoCanvas
 *
 * Props:
 *   videoRef            — ref attached to <video>
 *   sourceUrl           — URL of the original horizontal video
 *   cropRect            — { x, y, width, height } in SOURCE pixels
 *   onCropChange(rect)  — new crop in SOURCE pixels
 *   selectedSceneIndex
 *   scenes
 *   onDurationLoad
 *   onSourceSizeLoad
 *   sourceVideoSize     — { width, height } natural dims (from parent, used for frameRect)
 */
export default function VideoCanvas({
  videoRef,
  sourceUrl,
  cropRect,
  onCropChange,
  selectedSceneIndex,
  scenes,
  onDurationLoad,
  onSourceSizeLoad,
  sourceVideoSize,            // ← from parent (same value VerticalPreview uses)
}) {
  const containerRef = useRef(null);
  const [videoSize, setVideoSize] = useState({ width: 0, height: 0 });
  // Internal sourceVideoSize used ONLY for drag/resize scale math (needs real video dims)
  const [internalSrcSize, setInternalSrcSize] = useState({ width: 0, height: 0 });
  const cropRectRef = useRef(cropRect);
  const dragStartCropRef = useRef(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [zoom, setZoom] = useState(1.0);

  useEffect(() => { cropRectRef.current = cropRect; }, [cropRect]);

  // ---- video metadata ----
  const handleMetadata = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const w = video.videoWidth;
    const h = video.videoHeight;
    setInternalSrcSize({ width: w, height: h });
    setVideoLoaded(true);
    if (onDurationLoad) onDurationLoad(video.duration || 0);
    if (onSourceSizeLoad) onSourceSizeLoad(w, h);
  }, [videoRef, onDurationLoad, onSourceSizeLoad]);

  // ---- measure displayed video size ----
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoLoaded) return;
    const measure = () => {
      const w = video.offsetWidth;
      const h = video.offsetHeight;
      if (w > 0 && h > 0) setVideoSize({ width: w, height: h });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(video);
    return () => ro.disconnect();
  }, [videoLoaded, videoRef, zoom]);

  // ---- mouse wheel zoom ----
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = (e) => {
      e.preventDefault();
      setZoom((p) => Math.max(0.25, Math.min(3.0, p + (e.deltaY > 0 ? -0.1 : 0.1))));
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, []);

  // ---- drag/resize: snapshot the crop at pointer-down so move events apply
  // an ABSOLUTE delta from the start crop. Avoids accumulated rounding from
  // previous-pos delta tracking + state round-tripping.
  const handleDragStart = useCallback(() => {
    dragStartCropRef.current = cropRectRef.current ? { ...cropRectRef.current } : null;
  }, []);

  // ---- drag: move frame X and Y (absolute delta from drag-start) ----
  const handleDrag = useCallback((dx, dy) => {
    const start = dragStartCropRef.current;
    if (!start || !videoSize.width || !internalSrcSize.width) return;
    const sx = internalSrcSize.width / videoSize.width;
    const sy = internalSrcSize.height / videoSize.height;
    const newX = Math.max(0, Math.min(start.x + dx * sx, internalSrcSize.width - start.width));
    const newY = Math.max(0, Math.min(start.y + dy * sy, internalSrcSize.height - start.height));
    onCropChange({ ...start, x: newX, y: newY });
  }, [videoSize, internalSrcSize, onCropChange]);

  // ---- resize: maintain 9:16 ratio (absolute delta from drag-start) ----
  const handleResize = useCallback((handle, dx, dy) => {
    const start = dragStartCropRef.current;
    if (!start || !videoSize.width || !internalSrcSize.width) return;
    const sx = internalSrcSize.width / videoSize.width;
    const sy = internalSrcSize.height / videoSize.height;
    const sdx = dx * sx;
    const sdy = dy * sy;
    const MIN_W = Math.max(50, internalSrcSize.width * 0.05);

    let { x, y, width, height } = start;
    let nw = width, nh = height, nx = x, ny = y;

    switch (handle) {
      case "e": case "se":
        nw = Math.max(MIN_W, width + sdx); nh = nw / CROP_RATIO; break;
      case "w": case "sw":
        nw = Math.max(MIN_W, width - sdx); nh = nw / CROP_RATIO; nx = x + width - nw; break;
      case "s":
        nh = Math.max(MIN_W / CROP_RATIO, height + sdy); nw = nh * CROP_RATIO; break;
      case "n":
        nh = Math.max(MIN_W / CROP_RATIO, height - sdy); nw = nh * CROP_RATIO; ny = y + height - nh; break;
      case "ne":
        nw = Math.max(MIN_W, width + sdx); nh = nw / CROP_RATIO; ny = y + height - nh; break;
      case "nw":
        nw = Math.max(MIN_W, width - sdx); nh = nw / CROP_RATIO; nx = x + width - nw; ny = y + height - nh; break;
      default: return;
    }

    nx = Math.max(0, nx); ny = Math.max(0, ny);
    if (nx + nw > internalSrcSize.width)  { nw = internalSrcSize.width  - nx; nh = nw / CROP_RATIO; }
    if (ny + nh > internalSrcSize.height) { nh = internalSrcSize.height - ny; nw = nh * CROP_RATIO; }
    onCropChange({ x: nx, y: ny, width: nw, height: nh });
  }, [videoSize, internalSrcSize, onCropChange]);

  // ---- frameRect: use PARENT sourceVideoSize so it's identical to what VerticalPreview uses ----
  let frameRect = null;
  if (videoLoaded && videoSize.width && sourceVideoSize?.width && cropRect) {
    const scaleX = videoSize.width  / sourceVideoSize.width;
    const scaleY = videoSize.height / sourceVideoSize.height;
    frameRect = {
      x:      cropRect.x      * scaleX,
      y:      cropRect.y      * scaleY,
      width:  cropRect.width  * scaleX,
      height: cropRect.height * scaleY,
    };
  }

  // ---- video element max size based on zoom ----
  const baseMaxW = (window.innerWidth  - 340) * 0.55;
  const baseMaxH = (window.innerHeight - 260) * 0.55;

  return (
    <CanvasArea ref={containerRef}>
      {!sourceUrl && (
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.4)" }}>
          No video source available
        </Typography>
      )}

      {sourceUrl && (
        <VideoCanvasWrapper>
          <VideoElement
            ref={videoRef}
            src={sourceUrl}
            preload="auto"
            playsInline
            onLoadedMetadata={handleMetadata}
            onError={() => setError(true)}
            style={{
              maxWidth:  `${Math.round(baseMaxW * zoom)}px`,
              maxHeight: `${Math.round(baseMaxH * zoom)}px`,
              width: "auto",
              height: "auto",
              display: "block",
            }}
          />
          {videoLoaded && frameRect && (
            <DraggableFrame
              frameRect={frameRect}
              videoBounds={videoSize}
              onDragStart={handleDragStart}
              onDrag={handleDrag}
              onResizeStart={handleDragStart}
              onResize={handleResize}
            />
          )}
          {!videoLoaded && !error && (
            <CircularProgress
              size={32}
              sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", color: "#9b46e1" }}
            />
          )}
          {error && (
            <Typography
              variant="body2"
              sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", color: "rgba(255,100,100,0.8)" }}
            >
              Failed to load video
            </Typography>
          )}
        </VideoCanvasWrapper>
      )}

      {/* Zoom overlay — bottom-right corner of canvas */}
      <Box sx={{
        position: "absolute", bottom: 10, right: 10,
        display: "flex", alignItems: "center", gap: 0.25,
        bgcolor: "rgba(17,17,24,0.85)",
        border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: 1, px: 0.75, py: 0.25, zIndex: 5,
      }}>
        <IconButton size="small" onClick={() => setZoom(p => Math.max(0.25, p - 0.15))} sx={{ color: "rgba(255,255,255,0.65)", p: "2px" }}>
          <RemoveRounded sx={{ fontSize: 13 }} />
        </IconButton>
        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", fontFamily: "monospace", fontSize: "0.65rem", minWidth: 30, textAlign: "center", userSelect: "none" }}>
          {Math.round(zoom * 100)}%
        </Typography>
        <IconButton size="small" onClick={() => setZoom(p => Math.min(3.0, p + 0.15))} sx={{ color: "rgba(255,255,255,0.65)", p: "2px" }}>
          <AddRounded sx={{ fontSize: 13 }} />
        </IconButton>
        <Tooltip title="Fit">
          <IconButton size="small" onClick={() => setZoom(1.0)} sx={{ color: "rgba(255,255,255,0.35)", p: "2px" }}>
            <FitScreenRounded sx={{ fontSize: 13 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </CanvasArea>
  );
}
